'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import {
  fetchProvenanceFilesPage,
  provenanceKeys,
  type ProvenanceFilesFilters,
  type ProvenanceFilesPage,
} from '@/shared/cors-provenance-api';
import { useProvenanceSession } from './useProvenanceSession';

const DEFAULT_PAGE_SIZE = 100;

/**
 * TanStack ``useInfiniteQuery`` hook for ``GET /provenance-service/v1/files``.
 *
 * - ``filters`` map straight onto the FastAPI route's optional parameters
 *   (``searchQuery``, ``includeDeleted``, ``contentCategories``, ``tags``,
 *   ``tagMatch``); array filters become repeated query keys.
 * - Pages are offset/limit pages of ``FileSummary``. ``nextOffset`` mirrors
 *   the upstream ``pagination.hasMore`` flag (``null`` once all pages have
 *   been consumed); each page also carries the ``total`` count from the
 *   server.
 *
 * Consumers typically read the flattened list via
 * ``data?.pages.flatMap(p => p.items)`` and trigger more loads with
 * ``fetchNextPage()`` / ``hasNextPage``.
 *
 * The query is disabled until both ``cellId`` and ``realAccessToken`` are
 * available on ``AuthContext``.
 */
export function useProvenanceFiles(
  filters: ProvenanceFilesFilters = {},
  opts: { pageSize?: number; enabled?: boolean } = {},
) {
  const { cellId, accessToken, enabled: sessionEnabled } = useProvenanceSession();
  const pageSize = opts.pageSize ?? DEFAULT_PAGE_SIZE;
  const enabled = sessionEnabled && (opts.enabled ?? true);

  const query = useInfiniteQuery<ProvenanceFilesPage, Error>({
    queryKey: provenanceKeys.filesList(cellId, filters, pageSize),
    queryFn: ({ pageParam, signal }) =>
      fetchProvenanceFilesPage({
        cellId: cellId as string,
        accessToken: accessToken as string,
        offset: typeof pageParam === 'number' ? pageParam : 0,
        limit: pageSize,
        filters,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return Object.assign(query, { cellId });
}
